import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaPlay, FaCheck, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import apiClient from '../../apiClient';
import PhaseCard from './PhaseCard';
import ProgressIndicator from './ProgressIndicator';
import './ChangeCompass.css';

const ChangeCompassDashboard = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const isProjectSpecific = !!projectId; // Check if we're in project-specific mode
  
  const [instance, setInstance] = useState(null);
  const [instances, setInstances] = useState([]); // For general mode - all instances
  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchCompassData();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCompassData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isProjectSpecific) {
        // Project-specific mode - fetch specific project data
        const projectResponse = await apiClient.get(`/projects/${projectId}`);
        setProject(projectResponse.data);

        // Try to fetch existing instance for this project
        try {
          const instancesResponse = await apiClient.get('/change-compass/instances');
          const projectInstance = instancesResponse.data.find(inst => inst.project_id === parseInt(projectId));
          setInstance(projectInstance || null);
        } catch (instanceError) {
          console.log('No existing instance found, ready to create new one');
          setInstance(null);
        }
      } else {
        // General mode - fetch all instances for the user
        const instancesResponse = await apiClient.get('/change-compass/instances');
        setInstances(instancesResponse.data);
      }

      // Fetch Change Compass phases definition
      const phasesResponse = await apiClient.get('/change-compass/phases');
      setPhases(phasesResponse.data);

    } catch (err) {
      console.error('Error fetching Change Compass data:', err);
      setError(err.response?.data?.error || 'Failed to load Change Compass data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInstance = async () => {
    try {
      setCreating(true);
      setError(null);

      const response = await apiClient.post('/change-compass/instances', {
        project_id: parseInt(projectId)
      });

      setInstance(response.data.instance);
      
      // Show success message
      console.log('Change Compass instance created successfully');
    } catch (err) {
      console.error('Error creating Change Compass instance:', err);
      setError(err.response?.data?.error || 'Failed to create Change Compass instance');
    } finally {
      setCreating(false);
    }
  };

  const handlePhaseClick = (phaseNumber) => {
    navigate(`/change-compass/${projectId}/phase/${phaseNumber}`);
  };

  const getPhaseStatus = (phaseNumber) => {
    if (!instance || !instance.progress) return 'not-started';
    
    const phaseProgress = instance.progress.find(p => p.phase_number === phaseNumber);
    if (!phaseProgress) return 'not-started';
    
    if (phaseProgress.completion_percentage === 100) return 'completed';
    if (phaseProgress.completion_percentage > 0) return 'in-progress';
    if (instance.current_phase === phaseNumber) return 'current';
    if (instance.current_phase > phaseNumber) return 'completed';
    
    return 'not-started';
  };

  const getOverallProgress = () => {
    if (!instance) return 0;
    return Math.round(instance.overall_completion_percentage || 0);
  };

  if (loading) {
    return (
      <div className="change-compass-loading">
        <FaSpinner className="spinner" />
        <p>Loading Change Compass...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="change-compass-error">
        <FaExclamationTriangle className="error-icon" />
        <h3>Error Loading Change Compass</h3>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={fetchCompassData}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="change-compass-dashboard">
      {/* Header Section */}
      <div className="compass-header">
        <div className="compass-title">
          <h1>🧭 Change Compass</h1>
          <p className="compass-subtitle">Navigate change with confidence - from vision to victory</p>
        </div>
        
        {isProjectSpecific && project && (
          <div className="project-info">
            <h2>{project.name}</h2>
            <div className="project-meta">
              <span className="project-status">{project.status}</span>
              <span className="project-phase">{project.project_phase}</span>
            </div>
          </div>
        )}
        
        {!isProjectSpecific && (
          <div className="general-mode-info">
            <p>Manage Change Compass instances across all your projects</p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/projects')}
            >
              View All Projects
            </button>
          </div>
        )}
      </div>

      {/* General Mode - All Instances */}
      {!isProjectSpecific && instances.length > 0 && (
        <div className="compass-instances-list">
          <h3>Your Change Compass Instances</h3>
          <div className="instances-grid">
            {instances.map((inst) => (
              <div key={inst.id} className="instance-card" onClick={() => navigate(`/change-compass/${inst.project_id}`)}>
                <div className="instance-header">
                  <h4>{inst.project?.name || `Project ${inst.project_id}`}</h4>
                  <span className={`status-badge ${inst.status}`}>{inst.status}</span>
                </div>
                <div className="instance-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${inst.overall_completion_percentage || 0}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{Math.round(inst.overall_completion_percentage || 0)}% Complete</span>
                </div>
                <div className="instance-meta">
                  <span>Phase {inst.current_phase}/7</span>
                  <span>{new Date(inst.updated_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* General Mode - No Instances */}
      {!isProjectSpecific && instances.length === 0 && (
        <div className="compass-no-instances">
          <div className="no-instances-content">
            <div className="no-instances-icon">🧭</div>
            <h3>No Change Compass Instances Yet</h3>
            <p>
              Create Change Compass instances for your projects to start guided change management.
              Go to any project and click the compass icon to begin your change journey.
            </p>
            <button 
              className="btn btn-primary btn-large"
              onClick={() => navigate('/projects')}
            >
              <FaPlay />
              Go to Projects
            </button>
          </div>
        </div>
      )}

      {/* Progress Overview */}
      {isProjectSpecific && instance && (
        <div className="compass-overview">
          <ProgressIndicator
            currentPhase={instance.current_phase}
            overallProgress={getOverallProgress()}
            status={instance.status}
          />
          
          <div className="compass-stats">
            <div className="stat-item">
              <span className="stat-value">{getOverallProgress()}%</span>
              <span className="stat-label">Complete</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{instance.current_phase}</span>
              <span className="stat-label">Current Phase</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">
                {instance.progress ? instance.progress.reduce((sum, p) => sum + p.completed_tasks, 0) : 0}
              </span>
              <span className="stat-label">Tasks Complete</span>
            </div>
          </div>
        </div>
      )}

      {/* No Instance State */}
      {isProjectSpecific && !instance && (
        <div className="compass-no-instance">
          <div className="no-instance-content">
            <div className="no-instance-icon">🧭</div>
            <h3>Ready to Start Your Change Journey?</h3>
            <p>
              The Change Compass will guide you through the essential phases of change management,
              integrating with your project data and providing clear direction at every step.
            </p>
            <div className="compass-benefits">
              <div className="benefit-item">
                <FaCheck className="benefit-icon" />
                <span>7 structured phases from definition to closure</span>
              </div>
              <div className="benefit-item">
                <FaCheck className="benefit-icon" />
                <span>Seamless integration with project data</span>
              </div>
              <div className="benefit-item">
                <FaCheck className="benefit-icon" />
                <span>Progress tracking and task management</span>
              </div>
              <div className="benefit-item">
                <FaCheck className="benefit-icon" />
                <span>Links to stakeholders, assessments & training</span>
              </div>
            </div>
            <button 
              className="btn btn-primary btn-large"
              onClick={handleCreateInstance}
              disabled={creating}
            >
              {creating ? (
                <>
                  <FaSpinner className="spinner" />
                  Creating Compass...
                </>
              ) : (
                <>
                  <FaPlay />
                  Start Change Compass
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Phase Cards Grid */}
      {isProjectSpecific && instance && phases.length > 0 && (
        <div className="compass-phases">
          <h3>Change Management Phases</h3>
          <div className="phases-grid">
            {phases.map((phase) => (
              <PhaseCard
                key={phase.number}
                phase={phase}
                status={getPhaseStatus(phase.number)}
                progress={instance.progress?.find(p => p.phase_number === phase.number)}
                onClick={() => handlePhaseClick(phase.number)}
                isClickable={instance.current_phase >= phase.number || getPhaseStatus(phase.number) !== 'not-started'}
              />
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {isProjectSpecific && instance && (
        <div className="compass-actions">
          <h3>Quick Actions</h3>
          <div className="action-buttons">
            <button 
              className="btn btn-outline"
              onClick={() => navigate(`/projects/${projectId}`)}
            >
              View Project Details
            </button>
            <button 
              className="btn btn-outline"
              onClick={() => navigate(`/projects/${projectId}/stakeholders`)}
            >
              Manage Stakeholders
            </button>
            <button 
              className="btn btn-outline"
              onClick={() => navigate(`/assessments?project_id=${projectId}`)}
            >
              View Assessments
            </button>
            <button 
              className="btn btn-outline"
              onClick={() => navigate(`/training?project_id=${projectId}`)}
            >
              Training Resources
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeCompassDashboard; 